import { clsx, type ClassValue } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

// The design system ships semantic font-size utilities (`text-body-md`,
// `text-heading-lg`, …) that tailwind-merge does not know about, so without
// this extension it treats them as unrelated to `text-sm` and friends and
// keeps both when they conflict.
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      "font-size": [
        {
          text: [
            (value: string) =>
              ["heading", "body", "codeline", "display"].some((element) =>
                value.includes(element),
              ) &&
              ["xs", "sm", "md", "lg", "xl", "2xl"].some((element) =>
                value.includes(element),
              ),
          ],
        },
      ],
    },
  },
});

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

// __GRAM_SERVER_URL__ is the server's authoritative URL (injected at build
// time from GRAM_SERVER_URL). The viteprod profile leaves it unset, so fall
// back to the current origin instead of degrading to `undefined/rpc/...`.
// Use everywhere except the playground — MCP configs, callback URL
// displays, anything operator-facing.
export function getServerURL(): string {
  return __GRAM_SERVER_URL__ ?? window.location.origin;
}

// Base URL for the dashboard's SDK calls. In dev the dashboard and the server
// listen on different ports (and worktrees remap both), so every SDK call is
// cross-origin — and because the requests carry custom headers (gram-project,
// gram-session, …) each one pays a CORS preflight that the server never lets
// the browser cache. Vite proxies /rpc (and /chat, /mcp, …) to the server, so
// pointing the SDK at the dashboard's own origin in dev makes the calls
// same-origin and the preflights disappear. In prod the dashboard is served
// from the server's origin, so getServerURL() is already same-origin.
export function getApiBaseURL(): string {
  return import.meta.env.DEV ? window.location.origin : getServerURL();
}

// Hosts serving the production dashboard. Compared exactly: a substring check
// would also match dev.ai.speakeasy.com and preview hosts.
const PROD_HOSTS = new Set(["app.getgram.ai", "ai.speakeasy.com"]);

export function isProdHost(serverURL: string): boolean {
  return PROD_HOSTS.has(new URL(serverURL).hostname);
}

// Hosts serving the dev dashboard, compared exactly like PROD_HOSTS.
const DEV_HOSTS = new Set(["dev.getgram.ai", "dev.ai.speakeasy.com"]);

// Every first-party host that serves the same product as serverURL's host:
// all of that environment's hosts on a prod or dev host, otherwise just it.
export function firstPartyHosts(serverURL = getServerURL()): string[] {
  const host = new URL(serverURL, window.location.origin).hostname;
  if (PROD_HOSTS.has(host)) return [...PROD_HOSTS];
  if (DEV_HOSTS.has(host)) return [...DEV_HOSTS];
  return [host];
}

// Hosts an agent sandbox must allow to reach Speakeasy: the current host first,
// then every other host of its environment. Published plugins and the device
// daemon send to the server URL at publish time, which moves between these
// hosts when the server URL changes, so an allowlist needs all of them to keep
// working across a republish.
export function agentEgressHosts(serverURL = getServerURL()): string[] {
  const host = new URL(serverURL, window.location.origin).hostname;
  return [host, ...firstPartyHosts(serverURL).filter((h) => h !== host)];
}

// tunnel.speakeasy.com on every prod host, tunnel.dev.getgram.ai on every dev
// host, tunnel-pr-N.<env> in previews (single label keeps the wildcard cert
// valid), tunnel.<host> otherwise.
export function tunnelGatewayURL(serverURL = getServerURL()): string {
  const server = new URL(serverURL);
  const host = PROD_HOSTS.has(server.hostname)
    ? "tunnel.speakeasy.com"
    : DEV_HOSTS.has(server.hostname)
      ? "tunnel.dev.getgram.ai"
      : /^pr-\d+\./.test(server.host)
        ? `tunnel-${server.host}`
        : `tunnel.${server.host}`;
  return `${server.protocol === "http:" ? "ws" : "wss"}://${host}/connect`;
}

// __PLAYGROUND_PROXY_URL__ is the dashboard origin in dev (so browser-side MCP
// requests can ride the vite proxy and ferry cookies the Vercel AI SDK can't
// forward across origins), and undefined in prod. Consumed by the playground
// and the remote-MCP tools listing; everything else uses getServerURL().
export function getPlaygroundMcpBaseURL(): string {
  return __PLAYGROUND_PROXY_URL__ ?? getServerURL();
}

// mcpConnectionUrl rebases a display MCP URL (built from getServerURL) onto the
// dev proxy origin so a browser MCP client connects same-origin — the Vercel AI
// SDK doesn't carry credentials cross-origin, and the gateway's proxied SSE
// response doesn't survive the cross-origin hop. In prod the proxy base equals
// getServerURL so this is a no-op, and custom-domain URLs (a different origin)
// are left untouched.
export function mcpConnectionUrl(
  displayUrl: string | undefined,
): string | undefined {
  if (!displayUrl) return displayUrl;
  const base = getPlaygroundMcpBaseURL();
  const server = getServerURL();
  if (base === server) return displayUrl;
  try {
    const url = new URL(displayUrl);
    if (url.origin !== new URL(server).origin) return displayUrl;
    const baseUrl = new URL(base);
    url.protocol = baseUrl.protocol;
    url.host = baseUrl.host;
    return url.toString();
  } catch {
    return displayUrl;
  }
}

// Whether GET /mcp/<slug>/connect/first-party would resolve.
// The handler 404s unless the server is issuer-gated, is not a public tunnel
// (those serve anonymously and expose no OAuth surface), and the slug is a
// platform-namespace endpoint. A custom-domain slug is a different address
// and must not be passed as platformSlug.
export function supportsFirstPartyConnect(server: {
  userSessionIssuerId?: string;
  tunneledMcpServerId?: string;
  visibility?: string;
  platformSlug?: string;
}): boolean {
  if (!server.userSessionIssuerId) return false;
  if (server.tunneledMcpServerId && server.visibility === "public")
    return false;
  return !!server.platformSlug;
}

// firstPartyConnectUrl derives the runtime first-party connect entry point
// (`/mcp/<slug>/connect/first-party`) for a display MCP URL. It's
// always built on the Speakeasy server origin (getServerURL), never the display
// URL's origin: a custom-domain endpoint's display URL is
// `https://<customer-domain>/mcp/<slug>`, but the connect page is a Speakeasy auth
// surface — the IDP callback, routes, and any session live on the Speakeasy origin,
// not the customer's MCP domain. Pass a platform-namespace URL
// (`${getServerURL()}/mcp/<platform slug>`); a custom-domain display URL's
// slug is not looked up on that host. Opened as a top-level new tab; the IDP
// flow is state-based so no dev proxy is needed.
// Returns undefined when the slug can't be derived.
export function firstPartyConnectUrl(
  displayUrl: string | undefined,
): string | undefined {
  if (!displayUrl) return undefined;
  try {
    const slug = new URL(displayUrl).pathname.split("/").filter(Boolean).pop();
    if (!slug) return undefined;
    const url = new URL(getServerURL());
    url.pathname = `/mcp/${slug}/connect/first-party`;
    return url.toString();
  } catch {
    return undefined;
  }
}

export function buildLoginRedirectURL(
  redirectTo: string | null,
  orgName?: string,
  email?: string,
  partnerCode?: string,
): string {
  // The base is only consulted when getServerURL() is relative (empty in
  // tests, per vitest.config.ts) — window.location.origin is always
  // absolute, so `new URL` never throws here or in the browser.
  const url = new URL(
    `${getServerURL()}/rpc/auth.login`,
    window.location.origin,
  );
  if (redirectTo) url.searchParams.set("redirect", redirectTo);
  // Present only from the sign-up page. The server validates it, stashes it
  // against the login nonce, and creates the org during the auth callback.
  // As a query param on a top-level navigation it is visible in the address
  // bar, browser history, and access logs — acceptable for a company name,
  // which is not a secret, and it goes no further than this request.
  if (orgName) url.searchParams.set("org_name", orgName);
  // Also sign-up only. The server turns this into WorkOS's `login_hint`, which
  // pre-fills the email field on the hosted AuthKit screen. Speakeasy never writes
  // it to Redis or the database.
  //
  // It does land in request logs, though: the logger records full URLs, so
  // like `email` on agent.getPlugins this address reaches log sinks. Tracked
  // in AGE-3125, which covers both endpoints — the fixes differ, since this
  // hand-off is a top-level navigation and cannot use a header.
  if (email) url.searchParams.set("email", email);
  if (partnerCode?.trim())
    url.searchParams.set("partner_code", partnerCode.trim());
  return url.toString();
}

/**
 * True on macOS/iOS — used to pick ⌘ vs Ctrl in keyboard shortcut hints.
 * Defaults to true during SSR/tests where `navigator` is unavailable.
 */
export function isMacPlatform(): boolean {
  if (typeof navigator === "undefined") return true;
  return /mac|iphone|ipad|ipod/i.test(
    navigator.platform || navigator.userAgent,
  );
}

export function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Turn a URL slug into a Title Case display string, e.g.
 * "risk-policies" -> "Risk Policies". Intended for the static (literal) segments
 * of a route path — not for dynamic slug params like toolset or user
 * identifiers, which should keep their original casing.
 */
export function titleCaseSlug(slug: string): string {
  return slug.split("-").filter(Boolean).map(capitalize).join(" ");
}

export function assert(condition: unknown, message: string): asserts condition {
  if (!condition) {
    throw new Error(message);
  }
}

export function assertNever(value: unknown): never {
  throw new Error(`Unexpected value: ${String(value)}`);
}

/**
 * Two-letter initials from a display name or email handle: first letter of
 * the first and last word for a multi-word name ("Adam Bull" -> "AB"), or
 * the first two characters of the email's local part / a single-word name
 * otherwise ("adam@..." -> "AD").
 */
export function initialsOf(identifier: string): string {
  const handle = identifier.includes("@")
    ? (identifier.split("@")[0] ?? identifier)
    : identifier;
  const words = handle.trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return (
      words[0]!.charAt(0) + words[words.length - 1]!.charAt(0)
    ).toUpperCase();
  }
  return handle.trim().slice(0, 2).toUpperCase();
}

/** Sleep that respects AbortSignal for clean cancellation. */
export function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const onAbort = () => {
      clearTimeout(timeout);
      signal?.removeEventListener("abort", onAbort);
      reject(new DOMException("Aborted", "AbortError"));
    };
    const timeout = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

export function getCustomDomainCNAME(serverURL = getServerURL()): string {
  try {
    if (isProdHost(serverURL)) return "cname.getgram.ai.";
    const url = new URL(serverURL);
    if (DEV_HOSTS.has(url.hostname)) return "cname.dev.getgram.ai.";
    const parts = url.hostname.split(".");
    if (parts.length > 2) {
      parts[0] = parts[0] === "app" ? "cname" : `cname.${parts[0]}`;
    }
    return `${parts.join(".")}.`;
  } catch {
    return "cname.getgram.ai.";
  }
}

export function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  const step = Math.max(1, Math.floor(size));
  for (let i = 0; i < items.length; i += step) {
    out.push(items.slice(i, i + step));
  }
  return out;
}
